import { colorsPalettes } from "@similarweb/styles";

import { abbrNumberFilter, percentageSignFilter } from "app/filters/ngFilters";
import { buildAiTopicKeywordListId } from "app/modules/demand-analysis/keyword-lists";
// fallow-ignore-next-line boundary-violation -- reuses digital-suite home-page param defaults; extract to shared pending
import { getDefaultParamsForState } from "app/pages/digital-suite/home-page/helpers";
import type { HeatmapTopic } from "app/pages/market-research/api-client/topicsHeatmapAPI";
import type { Topic, TopicsRawData } from "app/pages/market-research/api-client/demandSearchTrendsTopicsAPI";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE } from "pro-features/pages/module start page/src/keyword analysis/consts";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const abbrNumber = abbrNumberFilter();
const percentageSign = percentageSignFilter();

export const volumeFormatter = (value: number): string => abbrNumber(value);

const FULL_PERCENT = 1;

export const changeFormatter = (value: number | null | undefined): string => {
    const absoluteChange = Math.abs(value ?? 0);
    // Above 100% change, decimals are just noise — show a whole number.
    const decimals = absoluteChange > FULL_PERCENT ? 0 : 2;
    return percentageSign(absoluteChange, decimals);
};

export type GrowthSentiment = "positive" | "negative" | "neutral";

export const getGrowthSentiment = (growth: string | undefined): GrowthSentiment => {
    if (growth === "Growing") return "positive";
    if (growth === "Declining") return "negative";
    return "neutral";
};

export type HeatmapTreemapNode = {
    id: string;
    name: string;
    value: number;
    color: string;
    customData: {
        volume: number;
        change: number | null;
        growth: string;
        trend: HeatmapTopic["trend"];
    };
};

const GROWTH_COLORS = {
    Growing: colorsPalettes.green[400],
    Declining: colorsPalettes.red[400],
    Stagnating: colorsPalettes.carbon[200],
} as const;

const MIN_OPACITY = 0.3;
const MAX_OPACITY = 1.0;

const hexToRgba = (hex: string, alpha: number): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const computeAbsoluteChange = (topic: HeatmapTopic): number => {
    const change = topic.volumeChange?.change ?? 0;
    const denominator = 1 + change;
    if (denominator === 0) return topic.volume;
    return Math.abs((topic.volume * change) / denominator);
};

const computeOpacity = (change: number, maxChange: number): number => {
    if (maxChange === 0) return MIN_OPACITY;
    const ratio = Math.min(Math.abs(change) / maxChange, 1);
    return MIN_OPACITY + ratio * (MAX_OPACITY - MIN_OPACITY);
};

export const getTopGrowingByVolume = (topics: HeatmapTopic[], count = 5): HeatmapTopic[] =>
    topics
        .filter((t) => t.volumeChange?.growth === "Growing" && t.volume > 0)
        .sort((a, b) => computeAbsoluteChange(b) - computeAbsoluteChange(a))
        .slice(0, count);

export const getTopGrowingByChange = (topics: HeatmapTopic[], count = 5): HeatmapTopic[] =>
    topics
        .filter((t) => t.volumeChange?.growth === "Growing" && t.volume > 0)
        .sort((a, b) => (b.volumeChange?.change ?? 0) - (a.volumeChange?.change ?? 0))
        .slice(0, count);

export const getTopDecliningByVolume = (topics: HeatmapTopic[], count = 5): HeatmapTopic[] =>
    topics
        .filter((t) => t.volumeChange?.growth === "Declining" && t.volume > 0)
        .sort((a, b) => computeAbsoluteChange(b) - computeAbsoluteChange(a))
        .slice(0, count);

export const getTopDecliningByChange = (topics: HeatmapTopic[], count = 5): HeatmapTopic[] =>
    topics
        .filter((t) => t.volumeChange?.growth === "Declining" && t.volume > 0)
        .sort((a, b) => (a.volumeChange?.change ?? 0) - (b.volumeChange?.change ?? 0))
        .slice(0, count);

export const buildTopicToCategoryMap = (topicsTree: TopicsRawData): Map<string, string> => {
    const map = new Map<string, string>();

    const collectLeafNames = (topic: Topic, categoryName: string): void => {
        map.set(topic.Name.toLowerCase(), categoryName);
        if (topic.Children) {
            Object.values(topic.Children).forEach((child) => collectLeafNames(child, categoryName));
        }
    };

    Object.entries(topicsTree).forEach(([, category]) => {
        collectLeafNames(category, category.Name);
    });

    return map;
};

export const filterTopicsByCategory = (
    topics: HeatmapTopic[],
    categoryName: string,
    topicToCategoryMap: Map<string, string>,
): HeatmapTopic[] => topics.filter((t) => topicToCategoryMap.get(t.name.toLowerCase()) === categoryName);

const MAX_SIZE_RATIO = 7;

const POWER = 0.5;
const clampVolume = (volume: number, minVolume: number, maxVolume: number): number => {
    if (maxVolume === minVolume) return 1;
    const normalized = (volume - minVolume) / (maxVolume - minVolume);
    return 1 + Math.pow(normalized, POWER) * (MAX_SIZE_RATIO - 1);
};

export type TreemapTransformResult = {
    nodes: HeatmapTreemapNode[];
    maxAbsChange: number;
};

export const computePointColor = (growth: string | undefined, change: number | null, maxAbsChange: number): string => {
    const baseColor = GROWTH_COLORS[(growth ?? "Stagnating") as keyof typeof GROWTH_COLORS] ?? GROWTH_COLORS.Stagnating;
    const opacity = computeOpacity(change ?? 0, maxAbsChange);
    return hexToRgba(baseColor, opacity);
};

export const transformToTreemapData = (topics: HeatmapTopic[]): TreemapTransformResult => {
    const validTopics = topics.filter((t) => t.volume > 0);
    const maxAbsChange = Math.max(...validTopics.map((t) => Math.abs(t.volumeChange?.change ?? 0)), 0.01);
    const volumes = validTopics.map((t) => t.volume);
    const minVolume = Math.min(...volumes, 1);
    const maxVolume = Math.max(...volumes, 1);

    const nodes = validTopics.map((topic) => {
        const growth = topic.volumeChange?.growth ?? "Stagnating";

        return {
            id: topic.name,
            name: topic.name,
            value: clampVolume(topic.volume, minVolume, maxVolume),
            color: computePointColor(growth, topic.volumeChange?.change ?? null, maxAbsChange),
            customData: {
                volume: topic.volume,
                change: topic.volumeChange?.change ?? null,
                growth,
                trend: topic.trend,
            },
        };
    });

    return { nodes, maxAbsChange };
};

export const navigateToTopic = (topicName: string): void => {
    const params = getDefaultParamsForState(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE);
    const keywordListId = buildAiTopicKeywordListId(topicName);

    void proNavigator.go(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE, {
        ...params,
        id: [keywordListId],
        country: WORLDWIDE_COUNTRY_ID,
    });
};
